<template>
    <BaseMapShell v-model:panelOpen="panelOpen" v-model:rightPanelOpen="rightPanelOpen" :center="center" :zoom="zoom"
        :bounds="bounds" :max-bounds="maxBounds" :minZoom="3" :maxZoom="19">

        <!-- Panneau : section Geofencings -->
        <template #panel-sections>
            <PanelSectionGeofencing :geofencings="geofencings" :selected-id="selectedGeofencingId"
                :loading="loadingGeofencingList || loadingGeofencingGeom" @refresh="fetchGeofencings"
                @select="handleSelectGeofencing" />
        </template>
        <!-- Calques spécifiques à cette page -->
        <template v-if="positions && positions.length">
            <SpeedLegend />
            <AlarmMarker v-for="alarm in alarms" :key="alarm.id" :alarm="alarm" />
            <SpeedPolylineLayer :polyline-data="polylineData" />
        </template>

        <!-- Geofencing sélectionné : polygone -->
        <l-layer-group v-if="selectedGeofencingPolygon && selectedGeofencingPolygon.length >= 3" layer-type="overlay"
            name="Geofencing" :visible="true">
            <l-polygon :lat-lngs="selectedGeofencingPolygon" :fill="true" :fillOpacity="0.15" :weight="3"
                :color="'#20c997'" />
        </l-layer-group>

        <!-- Contenu du Right Panel -->
        <template #right-panel>
            <RightHistoryPanel v-if="drive && drive.id" :balise="drive.balise" :drive="drive" :saving="corrCreateSaving"
                :successMsg="corrCreateSuccess" :errorMsg="corrCreateError"
                @create-corridoring="createCorridoringFromHistory"
                @reset-create-feedback="resetCorridoringCreateFeedback" />
        </template>
    </BaseMapShell>
</template>

<script>
import BaseMapShell from "../BaseMapShell.vue";
import AlarmMarker from "../Components/AlarmMarker.vue";
import RightHistoryPanel from "../Components/rightPanel/RightHistoryPanel.vue";
import PositionService from '@/services/position.service.js'
import AlarmService from '@/services/alarm.service.js'
import DriveService from '@/services/drive.service.js'
import FormatHelper from '@/helpers/format.helper.js'
import SpeedLegend from "../Components/SpeedLegend.vue";
import SpeedPolylineLayer from "../Components/SpeedPolylineLayer.vue";
import { getPolylineDataFromPositions } from "../composables/usePolyline";
import { computePaddedBounds } from "../composables/useBounds";
import PanelSectionGeofencing from "../Components/sidePanel/PanelSectionGeofencing.vue";
import { LLayerGroup, LPolygon } from "@vue-leaflet/vue-leaflet";
import GeofencingService from "@/services/geofencing.service.js";
import UserHelper from "@/helpers/user.helper.js";
import CorridoringService from "@/services/corridoring.service.js";

export default {
    components: {
        BaseMapShell,
        AlarmMarker,
        RightHistoryPanel,
        SpeedLegend,
        SpeedPolylineLayer,
        PanelSectionGeofencing,
        LLayerGroup,
        LPolygon,
    },
    watch: {
        $route: function () {
            this.getDriveInfos();
        }
    },
    data() {
        return {
            panelOpen: false,
            rightPanelOpen: true, // ✅ ouvert par défaut sur l’historique
            zoom: 13,
            center: [48.9571487, 2.1941876],
            bounds: [],
            maxBounds: [],
            drive: {},
            positions: [],
            alarms: [],
            polylineData: {},
            // Geofencings
            geofencings: [],
            selectedGeofencingId: null,
            selectedGeofencingPolygon: [],
            loadingGeofencingList: false,
            loadingGeofencingGeom: false,
            // ✅ Corridoring create (feedback panel droit)
            corrCreateSaving: false,
            corrCreateSuccess: "",
            corrCreateError: "",
        };
    },
    computed: {
        // Client pour les geofencings (même logique que sur la carte principale)
        effectiveClientId() {
            const fromStore = this.$store?.state?.auth?.user?.clientId ?? null;
            const fromWatcher = UserHelper?.getClientId?.() ?? null; // 0 = tous clients
            return fromStore ?? (fromWatcher && Number(fromWatcher) > 0 ? Number(fromWatcher) : null);
        },
    },
    mounted() {
        this.getDriveInfos();
        this.fetchGeofencings();
    },
    methods: {
        resetCorridoringCreateFeedback() {
            this.corrCreateSuccess = "";
            this.corrCreateError = "";
        },

        async createCorridoringFromHistory({ driveId, name }) {
            if (!this.$isAdmin) return;
            if (!driveId || !name) return;

            this.corrCreateSaving = true;
            this.corrCreateSuccess = "";
            this.corrCreateError = "";
            try {
                await CorridoringService.createCorridoring(driveId, name);
                this.corrCreateSuccess = "Corridoring créé.";
            } catch (e) {
                this.corrCreateError =
                    e?.response?.data?.message || e?.message || "Erreur lors de la création";
            } finally {
                this.corrCreateSaving = false;
            }
        },
        getDriveInfos() {
            DriveService.getDriveInfo(this.$route.params.driveid)
                .then(response => {
                    this.drive = FormatHelper.driveInfoFormat(response.data);
                    this.getPositions();
                    this.getAlarms();
                })
                .catch(e => {
                    console.log(e);
                });
        },
        getPositions() {
            PositionService.getBalisePositions(this.drive.baliseId, 0, 3000, this.drive.startDate, this.drive.endDate)
                .then(response => {
                    this.positions = FormatHelper.balisePingsFormat(response.data.filter(Array));
                    this.bounds = computePaddedBounds(this.positions, this.rightPanelOpen);
                    this.polylineData = getPolylineDataFromPositions(this.positions);
                })
        },
        getAlarms() {
            AlarmService.getBaliseAlarms(this.drive.baliseId, 0, 500, this.drive.startDate, this.drive.endDate)
                .then(response => {
                    this.alarms = FormatHelper.baliseAlertsFormat(response.data.rows);
                })
        },
        // --- Geofencings ---
        async fetchGeofencings(term) {
            if (!this.effectiveClientId) {
                this.geofencings = [];
                this.selectedGeofencingId = null;
                this.selectedGeofencingPolygon = [];
                return;
            }
            this.loadingGeofencingList = true;
            try {
                const search = (term || "").trim();
                const { data } = await GeofencingService.getClientGeofencings(this.effectiveClientId, search);
                this.geofencings = data || [];
                // si le geofencing sélectionné n’existe plus, on nettoie
                if (this.selectedGeofencingId) {
                    const exists = this.geofencings.some(g => g.id === this.selectedGeofencingId);
                    if (!exists) {
                        this.selectedGeofencingId = null;
                        this.selectedGeofencingPolygon = [];
                    }
                }
            } catch (e) {
                console.log(e);
            } finally {
                this.loadingGeofencingList = false;
            }
        },

        async handleSelectGeofencing(id) {
            this.selectedGeofencingId = id;
            this.selectedGeofencingPolygon = [];

            // Aucun geofencing sélectionné -> on laisse la vue telle quelle
            if (!id) return;

            this.loadingGeofencingGeom = true;
            try {
                const { data: pts } = await GeofencingService.getGeofencingPoints(id);

                const raw = (pts || [])
                    .slice()
                    .sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));

                // Nettoyage : conversion + filtre des points invalides
                const cleaned = raw
                    .map(p => ({
                        lat: Number(p.latitude),
                        lng: Number(p.longitude),
                    }))
                    .filter(p => Number.isFinite(p.lat) && Number.isFinite(p.lng));

                // Polygone pour Leaflet (array de [lat, lng])
                this.selectedGeofencingPolygon = cleaned.map(p => [p.lat, p.lng]);

                // Auto-zoom sur le geofencing sélectionné
                if (cleaned.length >= 1) {
                    this.bounds = computePaddedBounds(cleaned, this.rightPanelOpen);
                }
            } catch (e) {
                console.log(e);
            } finally {
                this.loadingGeofencingGeom = false;
            }
        },
    }
}
</script>